import type { FC, MouseEventHandler } from 'react';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Icon } from '@oneaudi/unified-web-components';
import { FAStateContext } from '../../context/FAStateContext';
import { createAddUrlParams } from '../../utils/addUrlParams';
import { LoadingIndicator } from './LoadingIndicator';
import { useTrackingManager } from '../../hooks/useTracking';
import { getRelatedProductInformation } from '../../utils/trackingUtils';
import { ContentContext } from '../../context/ContentContext';

const Button = styled.button`
  display: block;
  appearance: none;
  padding: 5px;
  background-color: transparent;
  border: none;
  position: relative;
  pointer-events: auto;
`;

interface ImageProps {
  readonly active?: boolean;
  readonly loadingState?: boolean;
  readonly equipmentType: 'color' | 'rim';
  readonly isLegacy?: string;
}

const Image = styled.img<ImageProps>`
  display: block;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, active, isLegacy }) => {
      if (isLegacy === 'true') {
        return active ? theme.colors.selectedIndicatorColor : 'transparent';
      }
      return active ? theme.colors.sysColorForegroundNeutral300 : 'transparent';
    }};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  padding: 2px;
  box-sizing: border-box;
  cursor: pointer;
  ${({ loadingState }) => (!loadingState ? '' : 'opacity: .5')}
  @media (min-width: ${(props) => props.theme.breakpoints.xs}px) {
    width: 36px;
    height: 36px;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.sm}px) {
    width: 36px;
    height: 36px;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.md}px) {
    width: 40px;
    height: 40px;
  }
`;

const StyledEditIcon = styled(Icon)`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: ${({ theme }) =>
    process.env.IS_LEGACY
      ? theme.colors.colorCanvasDefault
      : theme.colors.sysGradientElevationLevel3Fill};
  color: ${({ theme }) => theme.colors.sysColorSurfaceNeutral100};
  border-radius: 50%;
`;

export interface SelectorItemProps {
  readonly name: string;
  readonly imageUrl: string;
  readonly pr7String?: string;
  readonly active?: boolean;
  readonly showEditIcon?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly equipmentType?: 'color' | 'rim';
  readonly ariaLabel?: string;
}

export const SelectorItem: FC<SelectorItemProps> = ({
  name,
  pr7String,
  imageUrl,
  onClick,
  active = false,
  showEditIcon = false,
  equipmentType = 'color',
  ariaLabel,
}) => {
  const { loading, carline } = useContext(FAStateContext);
  const trackingManager = useTrackingManager();
  const { carlineId } = useContext(ContentContext);
  const ariaLabelText = ariaLabel ?? name;

  const clickHandler = useMemo((): MouseEventHandler<HTMLButtonElement> | undefined => {
    if (onClick) {
      return (e) => {
        if (active) {
          return;
        }

        e.currentTarget.scrollIntoView({
          inline: 'nearest',
          block: 'nearest',
          behavior: 'smooth',
        });

        const relatedProductInfo = pr7String
          ? getRelatedProductInformation(carline, [pr7String])
          : undefined;

        trackingManager.click({
          eventAction: 'content',
          eventName: `change ${equipmentType}`,
          elementName: 'icon',
          value: carlineId,
          relatedProducts: relatedProductInfo,
        });
        onClick(e);
      };
    }
    return undefined;
  }, [onClick]);

  const [url40, url80] = useMemo(() => {
    const addUrlParams = createAddUrlParams(imageUrl, 'webp');
    return [addUrlParams(40), addUrlParams(80)];
  }, [imageUrl]);

  return (
    <Button type="button" onClick={clickHandler} disabled={loading} aria-label={ariaLabelText}>
      {loading && active && <LoadingIndicator />}
      <Image
        active={active}
        loadingState={loading}
        alt=""
        src={url40}
        srcSet={`${url40} 40w, ${url80} 80w`}
        sizes="40px"
        width="30"
        height="30"
        loading="lazy"
        equipmentType={equipmentType}
        isLegacy={process.env.IS_LEGACY}
      />
      {showEditIcon && <StyledEditIcon name="edit" size="xs" />}{' '}
      {/* Conditionally render the edit icon */}
    </Button>
  );
};
